import React, { useState, useEffect, ReactElement } from 'react';
import lodashMerge from 'lodash.merge';
import { useTranslation } from 'react-i18next';
import { Table } from 'common/components/antd/Table/Table';
import { ELimitsPeriod } from 'domains/limits/enums/limits.enum';
import { useGetLimitsUsersDataQuery } from 'domains/limits/queries/limits.query';
import {
  TGetLimitsUsersAssetRow,
  TGetLimitsUsersDataResponse,
} from 'domains/limits/types/limits.types';
import { TSegmentsResponse } from 'domains/segments/types/segments.types';
import getPeriodTableColumns from './period-table.columns';
import { EPeriodTableColumns } from './period-table.enum';
import { TPeriodLimitsTableRecord } from './period-table.types';
import { ETypes } from '../../enums/limits.enum';
import SetLimitsModal from '../../modals/set-limits.modal';
import {
  TStandardLimitsModalData,
} from '../../types/standard-limits.types';

type TProps = {
  dataKey: string[][];
  tableType: ETypes;
  segmentsData: TSegmentsResponse;
};

const PeriodTableComponent = (props: TProps): ReactElement => {
  const {
    dataKey,
    tableType,
    segmentsData,
  } = props;
  const counterpartyData = dataKey[0];
  const counterpartyLabel = counterpartyData[0];
  const counterpartyGUID = counterpartyData[1];
  const segmentData = dataKey[1];
  const segmentLabel = segmentData[0];
  const segmentKey = segmentData[1];
  const { t } = useTranslation();
  const [modalData, setModalData] = useState<TStandardLimitsModalData | {}>({});

  const limitsUsersDataSelectQuery = (limitsUsersDataResponse: TGetLimitsUsersDataResponse): (any)[] => {
    const currentLimitsData = segmentsData.filter(
      currentLimitData => currentLimitData.counterparty.guid === counterpartyGUID
    )[0];
    const assetsList = currentLimitsData.segments[segmentKey];
    const formattedLimitsUsersData = (limitsUsersDataResponse?.columns || [])
      .reduce((limitsAcc: { [key: string]: TGetLimitsUsersAssetRow}, limitData: TGetLimitsUsersAssetRow) => ({
        ...limitsAcc,
        [limitData.account_type_guid]: limitData,
      }), {});
    const dataTableSkeleton = assetsList.map(({ account_type_guid, asset_code, asset_issuer }) => {
      const dataAssetRowFromApi = formattedLimitsUsersData[account_type_guid];
      const dataAssetRowSkeleton = {
        account_type_guid,
        asset_code,
        asset_issuer,
        period: {
          day: {
            period_key: 1,
            max: null,
          },
          days: {
            period_key: 1,
            max: null,
          },
          month: {
            period_key: 1,
            max: null,
          },
          week: {
            period_key: 1,
            max: null,
          },
          year: {
            period_key: 1,
            max: null,
          },
        },
      };

      return lodashMerge(dataAssetRowSkeleton, dataAssetRowFromApi);
    });

    return dataTableSkeleton;
  };

  const {
    data: limitsUsersData,
    isLoading: isLoadingLimitsUsersData,
    isError: isErrorLimitsUsersData,
    error: errorLimitsUsersData,
    refetch: refetchLimitsUsersData,
  } = useGetLimitsUsersDataQuery(
    counterpartyGUID,
    {
      select: limitsUsersDataSelectQuery,
      enabled: tableType === ETypes.User,
    }
  );

  useEffect(() => {
    if (isErrorLimitsUsersData) {
      console.error(errorLimitsUsersData);
    }
  }, [isErrorLimitsUsersData, errorLimitsUsersData]);

  const onCancelModalHandler = (): void => {
    setModalData({});
  };

  const invalidateDataTableHandler = async (): Promise<void> => {
    try {
      await refetchLimitsUsersData();
    } catch (error) {
      console.error(error);
    }
  };

  const onCellClickHandler = (record: TPeriodLimitsTableRecord, cellKey: string[]): void => {
    const periodKey = [...cellKey].pop() as string;
    const periodLabel = periodKey === ELimitsPeriod.Days
      ? t('limits.standardLimits.modal.edit.fields.label.period.days')
      : periodKey.charAt(0).toUpperCase() + periodKey.slice(1);
    const amount = cellKey?.reduce((acc: any, item: string) => acc[item], record);
    const maxAmount = amount?.max;
    const amountPeriodKey = amount?.period_key;

    const collectModalData: TStandardLimitsModalData = {
      accountTypeGuid: record.account_type_guid,
      counterpartyLabel,
      counterpartyGUID,
      segmentLabel,
      typeLabel: tableType,
      periodLabel,
      periodKey,
      maxAmount,
      minAmount: 0,
      amountPeriodKey,
      assetCode: record.asset_code,
      assetIssuer: record.asset_issuer,
    };
    setModalData(collectModalData);
  };

  const columns = getPeriodTableColumns(onCellClickHandler);

  return (
    <>
      <Table
        columns={columns}
        dataSource={limitsUsersData as TPeriodLimitsTableRecord[]}
        rowKey={EPeriodTableColumns.AccountTypeGuid}
        loading={isLoadingLimitsUsersData}
      />
      <SetLimitsModal
        isOpen={!!Object.keys(modalData).length}
        onCancelModalHandler={onCancelModalHandler}
        data={modalData as TStandardLimitsModalData}
        tableType={tableType}
        invalidateDataTableHandler={invalidateDataTableHandler}
      />
    </>
  );
};

export default PeriodTableComponent;
